/**
 * MilestoneShareCard — generic milestone / PR / streak share image generator.
 *
 * Reuses the same SVG → PNG → Web Share / Download / Copy pipeline as
 * PromotionShareCard, with flexible content slots so any milestone surface
 * (1RM PR, volume PR, workout count, streak, "first time" achievements) ships
 * with the same engine.
 *
 * Content slots:
 *   eyebrow     — small caps top line, e.g. "NEW PR · BENCH PRESS"
 *   headline    — large display copy, e.g. "FORGED HEAVIER"
 *   bigValue    — largest text on the card, e.g. "315 × 5"
 *   subline     — small detail under bigValue, e.g. "+10 lb from last cycle"
 *   athleteName — optional, pulled from profile
 *   accent      — hex color; defaults to enf-accent
 *   premium     — drops bottom watermark + tagline + URL
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Share2, Download, Copy, Check, X, Image as ImageIcon, Smartphone, Megaphone } from 'lucide-react';
import QRCode from 'qrcode';
import { taglineFor } from '../utils/taglines';
import { useProfile } from '../context/ProfileContext';

const xml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');

const renderCardSVG = ({ ratio, eyebrow, headline, bigValue, subline, athleteName, accent, premium, tagline, qrDataUrl }) => {
  const W = 1080;
  const H = ratio === 'story' ? 1920 : 1080;
  const safeAccent = accent || '#ef4444';

  // Vertical layout pinned to ratio
  const eyebrowY = ratio === 'story' ? 280 : 130;
  const headlineY = eyebrowY + 120;
  const bigValueY = ratio === 'story' ? 1020 : 600;
  const sublineY = bigValueY + 80;
  const athleteY = ratio === 'story' ? 1300 : 780;
  const wordmarkY = H - (ratio === 'story' ? 280 : 180);
  const taglineY = wordmarkY + 60;
  const urlY = taglineY + 50;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#0a0a0a" />
        <stop offset="100%" stop-color="#000000" />
      </linearGradient>
      <radialGradient id="accentGlow" cx="50%" cy="55%" r="55%">
        <stop offset="0%" stop-color="${safeAccent}" stop-opacity="0.30" />
        <stop offset="100%" stop-color="${safeAccent}" stop-opacity="0" />
      </radialGradient>
    </defs>

    <rect width="${W}" height="${H}" fill="url(#bg)" />
    <rect width="${W}" height="${H}" fill="url(#accentGlow)" />

    <!-- Decorative corner bars (subtle frame, brand accent) -->
    <rect x="60" y="60" width="120" height="6" fill="${safeAccent}" />
    <rect x="60" y="60" width="6" height="120" fill="${safeAccent}" />
    <rect x="${W - 180}" y="60" width="120" height="6" fill="${safeAccent}" />
    <rect x="${W - 66}" y="60" width="6" height="120" fill="${safeAccent}" />
    <rect x="60" y="${H - 66}" width="120" height="6" fill="${safeAccent}" />
    <rect x="60" y="${H - 180}" width="6" height="120" fill="${safeAccent}" />
    <rect x="${W - 180}" y="${H - 66}" width="120" height="6" fill="${safeAccent}" />
    <rect x="${W - 66}" y="${H - 180}" width="6" height="120" fill="${safeAccent}" />

    <!-- Eyebrow -->
    <text x="${W / 2}" y="${eyebrowY}" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="22" font-weight="700" letter-spacing="8" fill="${safeAccent}" text-anchor="middle">${xml(eyebrow)}</text>

    <!-- Headline -->
    <text x="${W / 2}" y="${headlineY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="${ratio === 'story' ? 130 : 110}" font-weight="900" letter-spacing="-1" fill="#ffffff" text-anchor="middle">${xml(headline)}</text>

    <!-- Big value — the focal point -->
    <text x="${W / 2}" y="${bigValueY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="${ratio === 'story' ? 220 : 180}" font-weight="900" letter-spacing="-4" fill="${safeAccent}" text-anchor="middle" filter="drop-shadow(0 0 20px ${safeAccent}66)">${xml(bigValue)}</text>

    <!-- Subline under the big number -->
    ${subline ? `<text x="${W / 2}" y="${sublineY}" font-family="ui-sans-serif, system-ui" font-size="36" fill="#d4d4d8" text-anchor="middle">${xml(subline)}</text>` : ''}

    <!-- Athlete name -->
    ${athleteName ? `<text x="${W / 2}" y="${athleteY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="48" font-weight="700" fill="#ffffff" text-anchor="middle">${xml(athleteName)}</text>` : ''}

    <!-- Bottom: wordmark + tagline + URL. Premium removes tagline + URL. -->
    <text x="${W / 2}" y="${wordmarkY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="${premium ? 48 : 68}" font-weight="900" letter-spacing="${premium ? 4 : 6}" fill="${premium ? '#52525b' : '#ffffff'}" text-anchor="middle">ENFERNO</text>
    ${premium ? '' : `<text x="${W / 2}" y="${taglineY}" font-family="ui-sans-serif, system-ui" font-size="26" fill="${safeAccent}" text-anchor="middle" font-style="italic">${xml(tagline || 'Bring the heat.')}</text>`}
    ${premium ? '' : `<text x="${W / 2}" y="${urlY}" font-family="ui-monospace, monospace" font-size="22" fill="#71717a" text-anchor="middle" letter-spacing="3">enferno.app</text>`}
    ${(!premium && qrDataUrl) ? `<image href="${qrDataUrl}" x="${W - 200}" y="${H - 200}" width="140" height="140" opacity="0.85" />` : ''}
  </svg>`;
};

const svgToPngBlob = async (svgStr, width, height) => {
  const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    return await new Promise((res) => canvas.toBlob(res, 'image/png', 0.95));
  } finally {
    URL.revokeObjectURL(url);
  }
};

const MilestoneShareCard = ({
  eyebrow,
  headline,
  bigValue,
  subline,
  athleteName,
  accent,
  premium = false,
  taglineContext = 'default',
  shareTitle = 'A milestone hit',
  shareText = 'New milestone on Enferno. Bring the heat. enferno.app',
  filenameBase = 'enferno-milestone',
  onClose,
}) => {
  const [ratio, setRatio] = useState('square');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [allowAmplify, setAllowAmplify] = useState(false);
  const { profile, addAmplificationConsent } = useProfile();

  const tagline = useMemo(() => taglineFor(taglineContext), [taglineContext]);

  // Deterministic card id per Recognition Layer 1. filenameBase is already
  // unique per milestone instance (e.g. enferno-pr-bench-press, enferno-25-sessions).
  const cardId = useMemo(() => `milestone-${taglineContext}-${filenameBase}`, [taglineContext, filenameBase]);
  const captureConsent = () => {
    if (allowAmplify) addAmplificationConsent({ cardId, type: taglineContext });
  };

  useEffect(() => {
    if (premium) return;
    let cancelled = false;
    // Welcome page is context-specific. taglineContext is one of:
    // 'pr' | 'streak' | 'sessions' (default fallback). The /welcome/:context
    // route mirrors the card visually + writes the share-ref into localStorage
    // for attribution at register-time.
    const welcomeSlug = ['pr', 'streak', 'sessions'].includes(taglineContext) ? taglineContext : 'pr';
    QRCode.toDataURL(`https://enferno.app/welcome/${welcomeSlug}`, {
      errorCorrectionLevel: 'M', margin: 1,
      color: { dark: '#ffffff', light: '#00000000' }, width: 280,
    }).then((url) => { if (!cancelled) setQrDataUrl(url); }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [premium, taglineContext]);

  const svgStr = useMemo(
    () => renderCardSVG({ ratio, eyebrow, headline, bigValue, subline, athleteName, accent, premium, tagline, qrDataUrl }),
    [ratio, eyebrow, headline, bigValue, subline, athleteName, accent, premium, tagline, qrDataUrl]
  );
  const width = 1080;
  const height = ratio === 'story' ? 1920 : 1080;
  const getBlob = async () => svgToPngBlob(svgStr, width, height);
  const filename = `${filenameBase}-${ratio}.png`;

  const flash = (msg) => { setStatus(msg); window.setTimeout(() => setStatus(''), 2500); };

  const triggerDownload = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      if (!blob) throw new Error('Render failed');
      const file = new File([blob], filename, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: shareTitle, text: shareText });
        captureConsent();
        flash('Shared');
      } else {
        triggerDownload(blob);
        captureConsent();
        flash('Saved — share sheet not supported on this device');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') flash('Share cancelled or failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      if (blob) { triggerDownload(blob); captureConsent(); flash('Downloaded'); }
    } finally { setBusy(false); }
  };

  const handleCopy = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      if (blob && navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        captureConsent();
        flash('Copied to clipboard');
      } else {
        if (blob) triggerDownload(blob);
        captureConsent();
        flash('Clipboard unsupported — downloaded instead');
      }
    } catch { flash('Copy failed'); } finally { setBusy(false); }
  };

  const previewSrc = useMemo(() => `data:image/svg+xml;utf8,${encodeURIComponent(svgStr)}`, [svgStr]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const supportsWebShare = typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [new File([new Blob()], 'test.png', { type: 'image/png' })] });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="milestone-share-card-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-1 flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> Share this milestone
            </div>
            <div className="font-display text-3xl tracking-wide leading-none">Take a victory lap.</div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mr-2">Format</span>
          <button onClick={() => setRatio('square')} data-testid="milestone-ratio-square" className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${ratio === 'square' ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-mid text-enf-text-secondary hover:bg-enf-surface-elevated'}`}>
            <ImageIcon className="w-3.5 h-3.5 inline mr-1.5" /> Square 1:1
          </button>
          <button onClick={() => setRatio('story')} data-testid="milestone-ratio-story" className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${ratio === 'story' ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-mid text-enf-text-secondary hover:bg-enf-surface-elevated'}`}>
            <Smartphone className="w-3.5 h-3.5 inline mr-1.5" /> Story 9:16
          </button>
        </div>

        <div className="mb-5 bg-enf-surface-mid rounded-lg overflow-hidden border border-enf-surface-border flex items-center justify-center" style={{ maxHeight: '60vh' }}>
          <img src={previewSrc} alt="Milestone share card preview" data-testid="milestone-card-preview" className={ratio === 'story' ? 'max-h-[60vh] w-auto' : 'w-full max-h-[60vh] object-contain'} />
        </div>

        {status && (
          <div className="mb-3 px-3 py-2 bg-enf-accent/10 border border-enf-accent-fg/30 text-enf-accent-fg text-sm rounded-md flex items-center gap-2">
            <Check className="w-4 h-4" /> {status}
          </div>
        )}

        {/* Amplification consent — Recognition Layer 1. Per-card opt-in, default off. */}
        <label className="mb-3 p-3 bg-enf-surface-deep border border-enf-surface-border rounded-md flex items-start gap-3 cursor-pointer hover:border-enf-surface-border transition-colors">
          <input
            type="checkbox"
            checked={allowAmplify}
            onChange={(e) => setAllowAmplify(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-enf-accent flex-shrink-0"
            data-testid="milestone-amplify-consent"
          />
          <span className="text-xs text-enf-text-secondary leading-relaxed">
            <span className="inline-flex items-center gap-1.5 text-enf-role-info font-semibold uppercase tracking-widest mr-1">
              <Megaphone className="w-3 h-3" /> Optional
            </span>
            Allow <span className="text-enf-text-primary font-semibold">@EnfernoApp</span> to amplify this card on our official channels.
            {!profile.socialHandle && (
              <span className="block mt-1 text-[11px] text-enf-text-muted italic">Set a @handle in your profile to be recognizable.</span>
            )}
          </span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button onClick={handleShare} disabled={busy} data-testid="milestone-share-button" className="py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
            <Share2 className="w-4 h-4" /> {supportsWebShare ? 'Share…' : 'Share / Save'}
          </button>
          <button onClick={handleDownload} disabled={busy} data-testid="milestone-download-button" className="py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" /> Download PNG
          </button>
          <button onClick={handleCopy} disabled={busy} data-testid="milestone-copy-button" className="py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
            <Copy className="w-4 h-4" /> Copy image
          </button>
        </div>
      </div>
    </div>
  );
};

export default MilestoneShareCard;
